/**
 * Wspólny arkusz rysunków teczki instalacji: formaty, ramka, miejsce na legendę i tabelkę, dobór skali.
 *
 * Styl z autorskiego projektu właściciela (`zadania/2026-10_teczka-instalacji.md`, „Wzór”): biała kartka, cienka
 * czarna kreska, ramka arkusza, legenda w prawym górnym rogu, tabelka w prawym dolnym. Wszystkie wymiary w mm
 * na papierze, początek w lewym górnym rogu arkusza, y w dół (rysowanie w pdf-lib przelicza je w `pdf.js`).
 *
 * Ramka jak w schemacie elektrycznym (`lib/schematJednokreskowy.js`): 20 mm z lewej na wpięcie, 10 mm z pozostałych
 * stron. Prawa kolumna (legenda, dane, tabelka) ma stałą szerokość na obu formatach, rysunek dostaje resztę.
 */

export const FORMATY = {
  A4: { klucz: 'A4', szer: 297, wys: 210 },
  A3: { klucz: 'A3', szer: 420, wys: 297 },
}

// Decyzja właściciela 2026-10-23: domyślnie A4, w podglądzie przełącznik A4/A3.
export const FORMAT_DOMYSLNY = 'A4'

export const MARGINES = { lewy: 20, gora: 10, prawy: 10, dol: 10 }
export const SZEROKOSC_KOLUMNY = 74
export const WYSOKOSC_TABELKI = 38
const ODSTEP_RYSUNKU = 3

/**
 * Układ arkusza w poziomie: `ramka`, prawa `kolumna` (legenda u góry, tabelka na dole) i `rysunek`,
 * czyli prostokąt na właściwy rysunek z wymiarami. Prostokąty { x, y, w, h } w mm.
 */
export const ukladArkusza = (format = FORMAT_DOMYSLNY) => {
  const f = FORMATY[format] || FORMATY[FORMAT_DOMYSLNY]
  const ramka = { x: MARGINES.lewy, y: MARGINES.gora, w: f.szer - MARGINES.lewy - MARGINES.prawy, h: f.wys - MARGINES.gora - MARGINES.dol }
  const kolumna = { x: ramka.x + ramka.w - SZEROKOSC_KOLUMNY, y: ramka.y, w: SZEROKOSC_KOLUMNY, h: ramka.h }
  const tabelka = { x: kolumna.x, y: ramka.y + ramka.h - WYSOKOSC_TABELKI, w: SZEROKOSC_KOLUMNY, h: WYSOKOSC_TABELKI }
  const rysunek = {
    x: ramka.x + ODSTEP_RYSUNKU, y: ramka.y + ODSTEP_RYSUNKU,
    w: kolumna.x - ramka.x - 2 * ODSTEP_RYSUNKU, h: ramka.h - 2 * ODSTEP_RYSUNKU,
  }
  return { format: f, ramka, kolumna, tabelka, rysunek }
}

/** Skale rysunku: 1:50 i 1:100 jak w projekcie właściciela, 1:200 i 1:500 tylko dla dużych dachów i gruntu. */
export const SKALE = [50, 100, 200, 500]

/** Milimetry na papierze na metr w terenie przy skali 1:s. */
export const mmNaMetr = (skala) => 1000 / skala

/**
 * Najdokładniejsza skala, w której rysunek mieści się w obszarze. `potrzebne(skala)` zwraca { w, h } w mm
 * na papierze (obiekt w skali plus wymiary dookoła, które zależą od skali przez rozkład napisów).
 * Gdy nie mieści się nawet w ostatniej skali, zwraca ostatnią z `miesci: false`.
 */
export const dobierzSkale = (potrzebne, obszar, skale = SKALE) => {
  for (const skala of skale) {
    const p = potrzebne(skala)
    if (p.w <= obszar.w + 1e-6 && p.h <= obszar.h + 1e-6) return { skala, miesci: true, ...p }
  }
  const skala = skale[skale.length - 1]
  return { skala, miesci: false, ...potrzebne(skala) }
}

/** Data na tabelkę: 23.10.2026. */
export const dataRysunku = (d = new Date()) => [d.getDate(), d.getMonth() + 1].map((x) => String(x).padStart(2, '0')).join('.') + `.${d.getFullYear()}`
